<template>
  <Transition name="back-to-top" :css="!atPageEnd">
    <button v-if="visible" type="button" class="back-to-top" :aria-label="$t('back_to_top')" :title="$t('back_to_top')" @click="scrollToTop">
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 19V5m-6 6 6-6 6 6" />
      </svg>
    </button>
  </Transition>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const scrolledDown = ref(false);
const atPageEnd = ref(false);
const visible = computed(() => scrolledDown.value && !atPageEnd.value);
let pageObserver;
let resizeFrame;
const updateVisibility = () => {
  const pageHeight = document.scrollingElement?.scrollHeight || document.documentElement.scrollHeight;
  const viewport = window.visualViewport;
  const viewportBottom = Math.max(
    window.scrollY + window.innerHeight,
    viewport ? viewport.pageTop + viewport.height : 0
  );
  scrolledDown.value = window.scrollY > 400;
  // Hide before reaching the bottom and throughout iOS rubber-band overscroll.
  atPageEnd.value = viewportBottom >= pageHeight - 24;
};
const scrollToTop = () => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, left: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
};

onMounted(() => {
  updateVisibility();
  pageObserver = new ResizeObserver(() => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(updateVisibility);
  });
  pageObserver.observe(document.body);
  window.addEventListener('scroll', updateVisibility, { passive: true });
  window.addEventListener('resize', updateVisibility);
  window.visualViewport?.addEventListener('scroll', updateVisibility, { passive: true });
  window.visualViewport?.addEventListener('resize', updateVisibility);
});
watch(() => route.fullPath, updateVisibility, { flush: 'post' });
onBeforeUnmount(() => {
  pageObserver?.disconnect();
  cancelAnimationFrame(resizeFrame);
  window.removeEventListener('scroll', updateVisibility);
  window.removeEventListener('resize', updateVisibility);
  window.visualViewport?.removeEventListener('scroll', updateVisibility);
  window.visualViewport?.removeEventListener('resize', updateVisibility);
});
</script>

<style scoped>
.back-to-top {
  position: fixed;
  z-index: 10000;
  right: max(24px, env(safe-area-inset-right));
  bottom: max(24px, env(safe-area-inset-bottom));
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #00092e;
  background: #ffcd00;
  box-shadow: 0 4px 20px #0005;
  cursor: pointer;
  transition: opacity .2s ease, transform .2s ease, box-shadow .2s ease;
}
.back-to-top svg { width: 26px; height: 26px; }
.back-to-top:focus-visible { outline: 3px solid #fff; outline-offset: 4px; }
.back-to-top-enter-from, .back-to-top-leave-to { opacity: 0; transform: translateY(12px); }
:global(body.blocked .back-to-top) { visibility: hidden; }
@media (hover: hover) {
  .back-to-top:hover { transform: translateY(-3px); box-shadow: 0 4px 24px #ffcd0066; }
}
@media (min-width: 1025px) {
  .back-to-top { right: 5%; }
}
@media (max-width: 640px) {
  .back-to-top { width: 46px; height: 46px; right: max(16px, env(safe-area-inset-right)); bottom: max(16px, env(safe-area-inset-bottom)); }
  .back-to-top svg { width: 24px; height: 24px; }
}
@media (prefers-reduced-motion: reduce) {
  .back-to-top { transition: none; }
}
</style>
